export class DateUtils {

  /**
   * Reconstruit un timestamp en UTC minuit à partir des composants
   * locaux d'une date (year/month/day), peu importe le fuseau du poste client.
   */
  static formatBirthDateToUTCTimestamp(value: any): number {
    const date = new Date(value);
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  }

  /**
   * Affiche un timestamp UTC en format DD/MM/YYYY, en lisant
   * les composants UTC (pas locaux) pour éviter le décalage de fuseau.
   */
  static displayDateNaissance(isoOrTimestamp: string | number): string {
    const d = new Date(isoOrTimestamp);
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }
}

/** Durée moyenne d'un mois (convention OMS) */
export const DAYS_PER_MONTH = 30.4375;

type DateInput = Date | string | number;

/**
 * Âge en mois FRACTIONNAIRES (ex : 1 mois 29 jours => ~1.97).
 * Utilisé pour la POSITION sur l'axe X.
 */
export function ageInMonths(birthDate: DateInput, atDate: DateInput = new Date()): number {
  const b = new Date(birthDate);
  const d = new Date(atDate);
  if (isNaN(b.getTime()) || isNaN(d.getTime())) return 0;

  const bUtc = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  const dUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((dUtc - bUtc) / 86400000);
  return Math.max(0, days / DAYS_PER_MONTH);
}

/** Ajoute n mois à une date en "clampant" le jour (31 janv + 1 mois => 28/29 févr) */
export function addMonthsClamped(d: Date, n: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastDay = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(d.getDate(), lastDay));
  return r;
}

/**
 * Âge CALENDAIRE exact : "1 mois 29 j".
 * Utilisé pour l'ÉTIQUETTE du tooltip (pas pour la position).
 */
export function formatAgeCalendar(birth: DateInput, at: DateInput): string {
  const b = new Date(birth);
  const d = new Date(at);
  if (isNaN(b.getTime()) || isNaN(d.getTime()) || d < b) return "0 mois";

  let m = (d.getFullYear() - b.getFullYear()) * 12 + (d.getMonth() - b.getMonth());

  let anchor = addMonthsClamped(b, m);
  if (anchor > d) anchor = addMonthsClamped(b, --m);

  const days = Math.round(
    (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) -
      Date.UTC(anchor.getFullYear(), anchor.getMonth(), anchor.getDate())) /
      86400000
  );

  return days > 0 ? `${m} mois ${days} j` : `${m} mois`;
}

/** 1.97 => "1 mois 30 j" (approximation OMS, pour fallback uniquement) */
export function formatAgeMonths(months: number): string {
  const safe = Math.max(0, Number(months) || 0);
  const m = Math.floor(safe);
  const d = Math.round((safe - m) * DAYS_PER_MONTH);
  return d > 0 ? `${m} mois ${d} j` : `${m} mois`;
}

/** Compare deux dates au jour près (gère string ISO, number, Date) */
export function sameDay(a: any, b: any): boolean {
  if (a == null || b == null || a === "" || b === "") return false;
  const da = new Date(a);
  const db = new Date(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
  return da.toDateString() === db.toDateString();
}